import { defineComponent as _defineComponent } from 'vue'
import { ref, onMounted, watch, onUnmounted, computed } from 'vue';

import {
  SkeletonLoader,
  HelperViewStatus,
  HelperViewContextual,
  BaseText
} from '@warrenbrasil/nebraska-web';
import { Accordion } from '@/modules/common/components/Accordion';
import { BrokerageNoteRow } from './components/BrokerageNoteRow';

import {
  PortfolioBrokerageNotesByMonth,
  getPortfolioBrokerageNotesList,
  PortfolioBrokerageNotesListFilters,
  PortfolioBrokerageNotesListFilterOption,
  PortfolioBrokerageNote
} from '@/modules/wealth/services/portfolio';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { getDailyBrokerageNote } from '@/modules/investments/services/trade';
import { forceDownloadHref } from '@/modules/common/helpers/force-download';
import { NotificationTypes } from '@/modules/common/services/http/notification';
import { notify } from '@/modules/common/helpers/notification';

interface Props {
  selectedYear?: number | string | null;
  portfolioId: string;
}
interface Emits {
  (
    event: 'on-load',
    portfolioName?: string | null,
    selectedYear?: number | string,
    yearOptions?: PortfolioBrokerageNotesListFilterOption[]
  ): void;
}

export default /*#__PURE__*/_defineComponent({
  __name: 'BrokerageNotes',
  props: {
    selectedYear: null,
    portfolioId: null
  },
  emits: ["on-load"],
  setup(__props: any, { emit }: { emit: ({
  (
    event: 'on-load',
    portfolioName?: string | null,
    selectedYear?: number | string,
    yearOptions?: PortfolioBrokerageNotesListFilterOption[]
  ): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  selectedYear?: number | string | null;
  portfolioId: string;
}





const isLoading = ref(false);
const hasError = ref(false);
const filters = ref<PortfolioBrokerageNotesListFilters | null>(null);
const portfolioName = ref<string | null>(null);
const months = ref<PortfolioBrokerageNotesByMonth[]>([]);
const abortSignal = ref<AbortController | null>(null);
const currBrokerageNoteDownloading = ref<string | null>(null);
const openedAccordionMonthsMap = ref<Record<string, boolean>>({});

watch(
  () => props.selectedYear,
  async () => {
    const currentYearOfFetchedData = getSelectedFilterOption(
      filters.value?.years || []
    )?.value;
    if (currentYearOfFetchedData !== props.selectedYear || hasError.value)
      await getBrokeragesNotes();
  }
);

const hasBrokerageNotes = computed(() => months.value.length > 0);

const isDownloadingBrokerageNote = computed(() => {
  return currBrokerageNoteDownloading.value !== null;
});

const downloadResource = async (url: string, filename: string) => {
  const data = await fetch(url, {
    headers: new Headers({
      Origin: location.origin
    }),
    mode: 'cors'
  });

  const blob = await data.blob();

  const blobUrl = window.URL.createObjectURL(blob);
  forceDownloadHref(blobUrl, filename);
};

const handleDownloadBrokerageNote = async (
  brokerageNote: PortfolioBrokerageNote
) => {
  currBrokerageNoteDownloading.value = brokerageNote.date.value;

  try {
    const data = await getDailyBrokerageNote(brokerageNote.file.url);

    await downloadResource(data.generatedURL, brokerageNote.date.formatted);
  } catch {
    notify({
      group: 'wrn-notifications',
      text: 'Não foi possível iniciar o download',
      type: NotificationTypes.error
    });
  } finally {
    currBrokerageNoteDownloading.value = null;
  }
};

const handleToggleAccordion = (isOpen: boolean, month: string) => {
  openedAccordionMonthsMap.value[month] = isOpen;
};

const getSelectedFilterOption = (
  options: PortfolioBrokerageNotesListFilterOption[]
) => {
  return options.find(option => option.active);
};

const setOpenedAccordionMonthsMap = () => {
  const map: Record<string, boolean> = {};

  months.value.forEach(month => {
    map[month.label] = false;
  });

  openedAccordionMonthsMap.value = map;
};

const getBrokeragesNotes = async () => {
  if (abortSignal.value) {
    abortSignal.value.abort();
  }
  abortSignal.value = new AbortController();

  try {
    isLoading.value = true;
    hasError.value = false;

    const params = {
      portfolioId: props.portfolioId,
      year: props.selectedYear
    };

    const data = await getPortfolioBrokerageNotesList(
      params,
      abortSignal.value.signal
    );

    portfolioName.value = data.portfolio.name;
    filters.value = data.filters;
    months.value = data.months;

    setOpenedAccordionMonthsMap();

    const selectedYear = getSelectedFilterOption(
      filters.value?.years || []
    )?.value;
    emit('on-load', portfolioName.value, selectedYear, filters.value?.years);
  } catch {
    emit('on-load');

    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

onMounted(async () => {
  await getBrokeragesNotes();
});

onUnmounted(() => {
  if (abortSignal.value) {
    abortSignal.value.abort();
  }
});

return { __sfc: true,props, emit, isLoading, hasError, filters, portfolioName, months, abortSignal, currBrokerageNoteDownloading, openedAccordionMonthsMap, hasBrokerageNotes, isDownloadingBrokerageNote, downloadResource, handleDownloadBrokerageNote, handleToggleAccordion, getSelectedFilterOption, setOpenedAccordionMonthsMap, getBrokeragesNotes, SkeletonLoader, HelperViewStatus, HelperViewContextual, BaseText, Accordion, BrokerageNoteRow, NebraskaColors }
}

})